/**
 * m3 echo Frontend - Authentication Service
 *
 * API client for authentication endpoints.
 *
 * Security improvements (v4.5.0):
 * - Refresh tokens handled via HttpOnly cookies
 * - withCredentials enabled for all auth requests
 * - Token refresh endpoint for automatic token renewal
 */

import axios from 'axios';
import type {
  LoginRequest,
  TokenResponse,
  ChangePasswordRequest,
  DisclaimerStatus,
  DisclaimerAcceptResponse,
} from '../types/api';

import { getApiBaseUrl } from './apiBaseUrl';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance for auth (no interceptors to avoid circular dependencies)
const authApi = axios.create({
  baseURL: `${API_BASE_URL}/api/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // IMPORTANT: Enable cookies for refresh token
});

export const authService = {
  /**
   * Login user with email and password
   * Sets refresh token in HttpOnly cookie (handled by browser)
   */
  login: async (credentials: LoginRequest): Promise<TokenResponse> => {
    const response = await authApi.post<TokenResponse>('/login', credentials);
    return response.data;
  },

  /**
   * Logout current user
   * Clears refresh token cookie
   */
  logout: async (token: string): Promise<void> => {
    await authApi.post(
      '/logout',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  /**
   * Refresh access token using HttpOnly refresh cookie
   * Cookie is automatically sent with withCredentials: true
   */
  refreshToken: async (): Promise<TokenResponse> => {
    const response = await authApi.post<TokenResponse>('/refresh');
    return response.data;
  },

  /**
   * Get current user info
   */
  getCurrentUser: async (token: string): Promise<TokenResponse> => {
    const response = await authApi.get<TokenResponse>('/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  /**
   * Change user password
   */
  changePassword: async (
    token: string,
    data: ChangePasswordRequest
  ): Promise<void> => {
    await authApi.post('/change-password', data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  /**
   * Check if user has a valid session by attempting to refresh
   * Used on app initialization to restore session after page reload
   */
  checkSession: async (): Promise<TokenResponse | null> => {
    try {
      const response = await authApi.post<TokenResponse>('/refresh');
      return response.data;
    } catch {
      // No valid session
      return null;
    }
  },

  /**
   * Check if user needs to accept the Early Access disclaimer
   * Should be called after successful login
   */
  getDisclaimerStatus: async (token: string): Promise<DisclaimerStatus> => {
    const response = await authApi.get<DisclaimerStatus>('/disclaimer-status', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  /**
   * Accept the Early Access disclaimer
   * Called when user clicks "Akzeptieren" in the disclaimer popup
   */
  acceptDisclaimer: async (token: string): Promise<DisclaimerAcceptResponse> => {
    const response = await authApi.post<DisclaimerAcceptResponse>(
      '/accept-disclaimer',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  },
};
